import React, { memo, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useStatus } from 'resift';
import _get from 'lodash/get';
import _cloneDeep from 'lodash/cloneDeep';
import _isNil from 'lodash/isNil';
import useEntityTypes, { PERSON_ENTITY_TYPE_KEY } from 'helpers/useEntityTypes';
import useGlobalConfig from 'helpers/useGlobalConfig';

// Component
import ProfileLayout from './ProfileLayout';

// Actions
import makeEntityTypesFetch from 'store/configuration/actions/entityTypesFetch';
import makeGlobalConfigFetch from 'store/configuration/actions/globalConfigFetch';
import makeUpdateLayoutFetch from './fetches/updateLayoutFetch';
import { BASE_FIELD_PAGE, BASE_FIELD_PAGE_COLLECTION } from 'pages/Attributes/consts';

function Container(props) {
  const globalConfigFetch = makeGlobalConfigFetch();
  const entityTypesFetch = makeEntityTypesFetch();

  const entityTypesStatus = useStatus(entityTypesFetch);
  const globalStatus = useStatus(globalConfigFetch);

  const entityTypes = useEntityTypes();
  const { typeKey } = props;
  const entityType = _get(entityTypes, typeKey);
  const globalConfig = useGlobalConfig();

  const isIndependentPage =
    _isNil(_get(entityType, 'personClientFieldId', null)) && typeKey !== PERSON_ENTITY_TYPE_KEY;
  const webProfileLayoutKey = typeKey === PERSON_ENTITY_TYPE_KEY ? 'web_profile' : 'profile-info';
  const webProfileLayout = _get(entityType, `_layouts.${webProfileLayoutKey}`, []);
  const profilePeopleLayout =
    typeKey === PERSON_ENTITY_TYPE_KEY ? _get(entityType, '_layouts.profile-people', []) : [];
  const updateLayoutFetch = makeUpdateLayoutFetch();

  const globalTempValue = {
    name: '',
    type: '',
    value: {
      id: '',
      displayName: '',
      entityField: '',
      children: [],
    },
  };
  const dispatch = useDispatch();

  const handleUpdateLayout = async data => {
    await dispatch(updateLayoutFetch(data));
    dispatch(entityTypesFetch());
  };

  // TODO Tyler:Revisit, same function in Category Card index.
  function getValidAttributes(category) {
    const categoryConfigObject = findCategory(category);
    const globalCategory = _get(categoryConfigObject, 'globalCategory', 'basic');
    let globalCategoryChildren = [];
    let matchingAttributes = [];
    globalConfig.categories[globalCategory].children.forEach(child => {
      globalCategoryChildren.push(child.name);
    });

    const fieldKeys = Object.keys(entityType._fields);
    globalCategoryChildren.push(BASE_FIELD_PAGE);
    globalCategoryChildren.push(BASE_FIELD_PAGE_COLLECTION);
    fieldKeys.forEach(fieldKey => {
      const baseField = entityType._fields[fieldKey].baseField;
      globalCategoryChildren.forEach(child => {
        if (child === baseField && !entityType._fields[fieldKey].objectKey.includes('.')) {
          matchingAttributes.push(entityType._fields[fieldKey]);
        }
      });
    });
    return matchingAttributes;
  }

  function findCategory(name) {
    const categoryKeys = Object.keys(entityType._categories);
    const categoryName = categoryKeys.find(field => field === name);
    const foundCategory = entityType._categories[categoryName];
    return foundCategory;
  }

  function buildCategory(value) {
    let tempValue = _cloneDeep(globalTempValue);
    const foundCategory = findCategory(value.name);
    tempValue.name = value.name;
    tempValue.type = value.type;
    tempValue.value.id = foundCategory.id;
    tempValue.value.displayName = foundCategory.displayName;
    tempValue.value.entityField = foundCategory.entityField;
    foundCategory.children.forEach(child => {
      if (child.type === 'category') {
        const newChild = buildCategory(child);
        tempValue.value.children.push(newChild);
      } else if (child.type === 'field') {
        const newChild = buildField(child);
        tempValue.value.children.push(newChild);
      }
    });
    return tempValue;
  }

  function findField(name) {
    const fieldKeys = Object.keys(entityType._fields);
    const fieldName = fieldKeys.find(field => field === name);
    const foundField = entityType._fields[fieldName];
    return foundField;
  }

  function buildField(value) {
    let tempValue = _cloneDeep(globalTempValue);
    const foundField = findField(value.name);
    tempValue.name = value.name;
    tempValue.type = value.type;
    tempValue.value.id = foundField.id;
    tempValue.value.displayName = foundField.displayName;
    tempValue.value.entityField = foundField.entityField;
    tempValue.value.restricted = foundField.restricted;
    tempValue.value.children = _get(foundField, 'children');
    return tempValue;
  }

  function buildBasicLayoutObject(layout) {
    const newBasicLayout = [];
    let returnValue;
    layout.forEach(value => {
      if (value.type === 'category') {
        returnValue = buildCategory(value);
      } else if (value.type === 'field') {
        returnValue = buildField(value);
      }
      newBasicLayout.push(returnValue);
    });
    return newBasicLayout;
  }

  const basicLayout = useMemo(() => buildBasicLayoutObject(webProfileLayout), [webProfileLayout]);
  const sidebarLayout = useMemo(() => buildBasicLayoutObject(profilePeopleLayout), [
    profilePeopleLayout,
  ]);

  return (
    <ProfileLayout
      {...props}
      typeKey={typeKey}
      sidebarLayout={sidebarLayout}
      entityType={entityType}
      entityTypesStatus={entityTypesStatus}
      globalConfig={globalConfig}
      globalStatus={globalStatus}
      isIndependentPage={isIndependentPage}
      getValidAttributes={getValidAttributes}
      layout={basicLayout}
      layoutItemTemplate={globalTempValue}
      handleUpdateLayout={handleUpdateLayout}
    />
  );
}

export default memo(Container);
